import { useTranslation } from "react-i18next";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { useNavigate } from "react-router-dom";

import {
	useAcceptCourseProgressInvitationMutation,
	useAuthorizeInScenarioProgressViaOtpMutation,
} from "@/pages/Private/redux/course-progress/course-progress.api";
import FormContainer from "@/pages/Public/components/FormContainer";
import { ERROR_TYPE } from "@/utils";

import FormOtpField from "@/pages/Public/components/FormOtpField";

import { otpSchema, OtpType } from "../schema/otp";

const PIN_ERRORS: string[] = [ERROR_TYPE.USER_NOT_FOUND, ERROR_TYPE.INVALID_OTP];

interface OtpFormProps {
	setShowFirstLastNameFields: (show: boolean) => void;
	setToken: (token: string | null) => void;
	setCourseProgressId: (courseProgressId: string | null) => void;
}

export default function OtpForm({
	setShowFirstLastNameFields,
	setCourseProgressId,
	setToken,
}: OtpFormProps) {
	const [authorizeViaOtp, { isLoading: isAuthViaOtpLoading, error: authorizeViaOtpError }] =
		useAuthorizeInScenarioProgressViaOtpMutation();
	const { t } = useTranslation();
	const ts = (key: string) => t(`courses.progress.${key}`);

	const navigate = useNavigate();
	const {
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm<OtpType>({
		defaultValues: { otp: "" },
		resolver: zodResolver(otpSchema),
	});

	const [acceptInvitation] = useAcceptCourseProgressInvitationMutation();

	const onSubmitViaOtp = async (values: OtpType) => {
		try {
			if (!values?.otp) {
				return;
			}

			const result = await authorizeViaOtp({ otpCode: +values.otp }).unwrap();

			if (result.isDefaultParticipant && result.firstName) {
				const accepted = await acceptInvitation({
					user: {
						firstName: result.firstName,
						lastName: result.lastName ?? "",
					},
					accessToken: result.accessToken,
					courseProgressId: result.courseProgressId,
				}).unwrap();

				if (accepted.courseProgressId) {
					navigate(`/app/scenarios/${accepted.courseProgressId}/progress`);
				}

				return;
			}

			if (!result.isFirstLogin) {
				navigate(`/app/scenarios/${result.courseProgressId}/progress`);
			} else {
				setShowFirstLastNameFields(true);
				setToken(result.accessToken);
				setCourseProgressId(result.courseProgressId);
			}
		} catch (err) {
			console.error(err);
		}
	};

	// A wrong or expired PIN gets this page's own wording instead of the generic error texts.
	const errorCode = (authorizeViaOtpError as { data?: { message?: string } } | undefined)?.data
		?.message;

	return (
		<FormContainer
			description={ts("first-login.otp-description")}
			formError={
				errorCode && PIN_ERRORS.includes(errorCode)
					? ts(`first-login.${errorCode}`)
					: authorizeViaOtpError
			}
			handleSubmit={handleSubmit(onSubmitViaOtp)}
			isLoading={isAuthViaOtpLoading}
			submitButtonText={ts("first-login.next")}
			title={ts("first-login.otp-title")}
		>
			<FormOtpField
				errorMessage={errors?.otp?.message && ts(`first-login.${errors.otp.message}`)}
				isFormError={!!authorizeViaOtpError}
				onChange={(otp) => setValue("otp", otp)}
			/>
		</FormContainer>
	);
}
